import { useId } from 'react'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'

import { colors } from '@gto/ui/theme/design-tokens'

type SparklineProps = {
  /** Ordered series of numeric values, oldest → newest. */
  data: number[]
  /**
   * Colour intent for the line/fill. Intentionally driven by the KPI's
   * period-over-period delta direction (green up / red down), NOT by the
   * shape of this series — so a tile whose metric grew shows a green
   * sparkline even if the series dips within the window.
   */
  direction?: 'up' | 'down' | 'neutral'
  height?: number
  className?: string
}

const toPoints = (data: number[]) => data.map((value, index) => ({ index, value }))

export const Sparkline = ({
  data,
  direction = 'neutral',
  height = 28,
  className,
}: SparklineProps) => {
  const gradientId = useId()

  if (data.length < 2) {
    return null
  }

  const colorByDirection: Record<NonNullable<SparklineProps['direction']>, string> = {
    up: colors.success.main,
    down: colors.error.main,
    neutral: colors.text.secondary,
  }
  const color = colorByDirection[direction]

  return (
    <div
      className={className}
      data-tst="sparkline"
    >
      <ResponsiveContainer
        height={height}
        width="100%"
      >
        <AreaChart
          data={toPoints(data)}
          margin={{ top: 2, right: 0, bottom: 2, left: 0 }}
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={color}
                stopOpacity={0.2}
              />
              <stop
                offset="100%"
                stopColor={color}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="value"
            dot={false}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
            stroke={color}
            strokeWidth={1.5}
            type="monotone"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
